import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Divider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Chip,
  TextField as MuiTextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaymentsIcon from "@mui/icons-material/Payments";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormik } from "formik";
import * as Yup from "yup";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";

interface WashData {
  id: number;
  vehicle?: {
    regNo: string;
    customer?: { name: string; phone: string; id: number };
  };
  actualAmount?: number | null;
  washStatus?: string;
  paymentStatus?: string;
}

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  wash: WashData | null;
  onSuccess: () => void;
}

export default function PaymentDialog({
  open,
  onClose,
  wash,
  onSuccess,
}: PaymentDialogProps) {
  const [applyOffer, setApplyOffer] = useState(false);
  const [offerData, setOfferData] = useState<{
    offerStatus: boolean;
    offerPercentage?: number;
    washCount?: number;
    eligibleAt?: number;
  } | null>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loadingOffer, setLoadingOffer] = useState(false);
  const amountEditedRef = useRef(false);

  const formik = useFormik({
    initialValues: {
      actualAmount: "",
      payments: [{ amount: "", accountId: "" }],
    },
    validationSchema: Yup.object({
      actualAmount: Yup.string().required("Actual amount is required"),
      payments: Yup.array().of(
        Yup.object({
          amount: Yup.string().required("Amount is required"),
          accountId: Yup.string().required("Bank account is required"),
        }),
      ),
    }),
    onSubmit: async (values) => {
      if (!wash) return;
      try {
        await request.post("/api/completePayment", {
          washId: wash.id,
          isOfferApplied: applyOffer ? 1 : 0,
          offerAmount: applyOffer ? offerAmount : 0,
          actualAmount: values.actualAmount,
          payments: values.payments.map((p) => ({
            amount: Number(p.amount),
            accountId: Number(p.accountId),
          })),
        });
        toast.success("Payment completed successfully!");
        onSuccess();
        onClose();
      } catch {
        toast.error("Failed to complete payment");
      }
    },
  });

  const addPaymentRow = () => {
    formik.setFieldValue("payments", [
      ...formik.values.payments,
      { amount: "", accountId: "" },
    ]);
  };

  const removePaymentRow = (index: number) => {
    if (formik.values.payments.length <= 1) return;
    const updated = formik.values.payments.filter((_, i) => i !== index);
    formik.setFieldValue("payments", updated);
  };

  const updatePaymentRow = (index: number, field: string, value: string) => {
    if (field === "amount") amountEditedRef.current = true;
    formik.setFieldValue(`payments[${index}].${field}`, value);
  };

  const getRowError = (index: number, field: string) => {
    const errors = formik.errors.payments as any;
    const touched = formik.touched.payments as any;
    return touched?.[index]?.[field] ? errors?.[index]?.[field] : undefined;
  };

  const offerAmount =
    offerData?.offerStatus && offerData?.offerPercentage
      ? Math.round(
          (Number(formik.values.actualAmount || 0) *
            offerData.offerPercentage) /
            100,
        )
      : 0;

  const totalPayed = formik.values.payments.reduce(
    (sum, p) => sum + (Number(p.amount) || 0),
    0,
  );
  const amountDue = Math.max(
    Number(formik.values.actualAmount || 0) - (applyOffer ? offerAmount : 0),
    0,
  );
  const remaining = amountDue - totalPayed;

  useEffect(() => {
    if (open && wash) {
      formik.resetForm();
      setApplyOffer(false);
      setOfferData(null);
      amountEditedRef.current = false;
      const amount = wash.actualAmount != null ? String(wash.actualAmount) : "";
      formik.setFieldValue("actualAmount", amount);
      formik.setFieldValue("payments", [{ amount, accountId: "" }]);
      fetchAccounts();
      checkOffer();
    }
  }, [open, wash]);

  useEffect(() => {
    const actual = formik.values.actualAmount;
    if (!actual) return;
    if (amountEditedRef.current) return;
    const target = applyOffer && offerAmount > 0
      ? Math.max(Number(actual) - offerAmount, 0)
      : Number(actual);

    const payments = formik.values.payments;
    const sum = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    if (sum === target) return;

    // Auto-fill only when a single row is present and it has not been edited
    if (payments.length === 1) {
      formik.setFieldValue("payments[0].amount", String(target));
    }
  }, [formik.values.actualAmount, applyOffer, offerAmount]);

  const checkOffer = async () => {
    const customerId = wash?.vehicle?.customer?.id;
    if (!customerId) return;
    setLoadingOffer(true);
    try {
      const res = await request.get("/api/checkCustomerOffer", { customerId });
      if (res?.data?.data) {
        setOfferData(res.data.data);
      }
    } catch {
      setOfferData(null);
    } finally {
      setLoadingOffer(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await request.get("/api/staff/getActiveAccounts");
      if (res?.data?.accounts) {
        setAccounts(res.data.accounts);
      }
    } catch {
      setAccounts([]);
    }
  };

  if (!wash) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #10b981, #059669)",
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <PaymentsIcon sx={{ fontSize: 22, color: "#fff" }} />
        <Box
          sx={{ flex: 1, color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}
        >
          Complete Payment
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: "rgba(255,255,255,0.8)", p: 0.5 }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
          >
            <Info label="Reg No" value={wash.vehicle?.regNo || "-"} />
            <Info
              label="Customer"
              value={wash.vehicle?.customer?.name || "-"}
            />
          </Box>

          <Divider sx={{ borderStyle: "dashed", opacity: 0.5 }} />

          <TextField
            name="actualAmount"
            label="Actual Amount"
            type="number"
            formik={formik}
          />

          {loadingOffer ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "0.78rem",
                color: "#64748b",
              }}
            >
              <CircularProgress size={12} />
              Checking offer...
            </Box>
          ) : offerData?.offerStatus ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: "rgba(16, 185, 129, 0.08)",
                border: "1px solid rgba(16, 185, 129, 0.2)",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={applyOffer}
                    onChange={(e) => setApplyOffer(e.target.checked)}
                  />
                }
                label={
                  <Box
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      color: "#065f46",
                    }}
                  >
                    Apply {offerData.offerPercentage}% off — Save ₹{offerAmount}
                  </Box>
                }
              />
            </Box>
          ) : offerData && !offerData.offerStatus ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: "rgba(245, 158, 11, 0.1)",
                border: "1px solid rgba(245, 158, 11, 0.2)",
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "rgba(245, 158, 11, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: "#d97706" }}>local_offer</span>
              </Box>
              <Box sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#92400e", lineHeight: 1.4 }}>
                {offerData.eligibleAt && offerData.washCount != null
                  ? `${offerData.eligibleAt - offerData.washCount} more wash${offerData.eligibleAt - offerData.washCount > 1 ? "es" : ""} to go for an offer`
                  : "No offer available"}
              </Box>
            </Box>
          ) : null}

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Box
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                Payments
              </Box>
              <Button
                size="small"
                onClick={addPaymentRow}
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  color: "#059669",
                  px: 0.75,
                  py: 0.25,
                  minWidth: 0,
                  borderRadius: 1.5,
                }}
              >
                Add Row
              </Button>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {formik.values.payments.map((payment, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "150px 200px auto",
                    gap: 1,
                    alignItems: "start",
                    justifyContent: "start",
                  }}
                >
                  <Box>
                    <MuiTextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Amount"
                      placeholder="0.00"
                      name={`payments[${index}].amount`}
                      value={payment.amount}
                      onChange={(e) =>
                        updatePaymentRow(index, "amount", e.target.value)
                      }
                      onBlur={formik.handleBlur}
                      error={Boolean(getRowError(index, "amount"))}
                      helperText={getRowError(index, "amount") as string}
                      sx={{ mt: 0, mb: 0 }}
                    />
                  </Box>

                  <Box>
                    <MuiTextField
                      fullWidth
                      select
                      size="small"
                      // label="Bank Account"
                      name={`payments[${index}].accountId`}
                      value={payment.accountId}
                      onChange={(e) =>
                        updatePaymentRow(index, "accountId", e.target.value)
                      }
                      shrink={true}
                      onBlur={formik.handleBlur}
                      error={Boolean(getRowError(index, "accountId"))}
                      helperText={getRowError(index, "accountId") as string}
                      SelectProps={{ displayEmpty: true, shrink: true }}
                      sx={{ mt: 0, mb: 0 }}
                    >
                      <MenuItem value="" disabled>
                        Select account
                      </MenuItem>
                      {accounts.map((acc: any) => (
                        <MenuItem key={acc.id} value={String(acc.id)}>
                          {`${acc.accountName}${acc.accountNo ? ` (${acc.accountNo})` : ""} — ${acc.accountType}`}
                        </MenuItem>
                      ))}
                    </MuiTextField>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => removePaymentRow(index)}
                    disabled={formik.values.payments.length <= 1}
                    sx={{
                      mt: 1.5,
                      width: 32,
                      height: 32,
                      color: "#ef4444",
                      "&:disabled": { color: "#cbd5e1" },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                mt: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1,
                borderRadius: 1.5,
                bgcolor: "rgba(0,0,0,0.03)",
              }}
            >
              <Box
                sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b" }}
              >
                Total Payed
              </Box>
              <Box
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color:
                    remaining > 0 ? "#dc2626" : totalPayed > 0 ? "#059669" : "#64748b",
                }}
              >
                ₹{totalPayed.toFixed(2)}
                {remaining !== 0 && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      color: remaining > 0 ? "#dc2626" : "#059669",
                      ml: 0.75,
                    }}
                  >
                    ({remaining > 0 ? `${remaining.toFixed(2)} due` : `overpaid ${Math.abs(remaining).toFixed(2)}`})
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            borderColor: "rgba(0,0,0,0.12)",
            color: "#374151",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => formik.handleSubmit()}
          variant="contained"
          size="small"
          disabled={formik.isSubmitting}
          startIcon={
            formik.isSubmitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <PaymentsIcon />
            )
          }
          sx={{
          background: "linear-gradient(135deg, #10b981, #059669)",
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 1.5,
            px: 2,
            "&:hover": {
              background: "linear-gradient(135deg, #059669, #047857)",
            },
          }}
        >
          {formik.isSubmitting ? "Processing..." : "Complete Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Box
        sx={{
          fontSize: "0.65rem",
          fontWeight: 700,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: 0.3,
          mb: 0.15,
        }}
      >
        {label}
      </Box>
      <Box sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#1c1b1f" }}>
        {value}
      </Box>
    </Box>
  );
}
